import { useCallback, useEffect, useState } from "react";
import { Inbox } from "lucide-react";
import { api, errText } from "@/lib/api";
import { useCustomer } from "@/lib/customer";
import { Mono } from "@/components/Badges";
import PoGroupRow from "@/components/PoGroupRow";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const BUCKETS = [
  { key: "overdue", title: "Overdue", note: "The clock has already run out", colours: ["red"] },
  { key: "week", title: "Due this week", note: "Inside the warning window", colours: ["amber"] },
  { key: "ontime", title: "On time", note: "Still comfortable", colours: ["green", "grey"] },
];

export default function MyDay() {
  const { customerId } = useCustomer();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState({});
  const [openStage, setOpenStage] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    () =>
      api
        .get("/po-groups", { params: { mine: true } })
        .then(({ data }) => setGroups(data.groups))
        .catch((e) => toast.error(errText(e)))
        .finally(() => setLoading(false)),
    []
  );

  useEffect(() => {
    load();
    const refresh = () => {
      if (!document.hidden) load();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load, customerId]); // RELOAD_ON_CUSTOMER

  return (
    <div className="space-y-4" data-testid="my-day-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">My day</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {user?.name}, the worst clocks sit on top. Open a PO to see where it stands and record the update right
          there.
        </p>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!loading && groups.length === 0 && (
        <div className="panel flex flex-col items-center gap-2 p-10 text-center" data-testid="my-day-empty">
          <Inbox className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium">Nothing open against your name</p>
          <p className="text-xs text-muted-foreground">
            New PO lines you create land here, and the manager can reassign work to you.
          </p>
        </div>
      )}

      {BUCKETS.map((bucket) => {
        const rows = groups.filter((g) => bucket.colours.includes(g.ldColour));
        if (rows.length === 0) return null;
        return (
          <div key={bucket.key} data-testid={`my-day-bucket-${bucket.key}`}>
            <div className="mb-2 flex items-baseline gap-2">
              <h2 className="text-base font-medium md:text-lg">{bucket.title}</h2>
              <span className="text-xs text-muted-foreground">
                <Mono>{rows.length}</Mono> · {bucket.note}
              </span>
            </div>
            <div className="panel divide-y divide-border overflow-hidden">
              {rows.map((group) => (
                <PoGroupRow
                  key={group.key}
                  group={group}
                  isOpen={!!open[group.key]}
                  toggle={() => setOpen((o) => ({ ...o, [group.key]: !o[group.key] }))}
                  openStage={openStage[group.key] ?? null}
                  setOpenStage={(stage) => setOpenStage((s) => ({ ...s, [group.key]: stage }))}
                  reload={load}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
